import { get } from 'lodash';
import React, { useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Form, Label } from 'semantic-ui-react';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import LimitControl from 'src/pages/ptp/LimitControl';
import { getDisplayValue } from 'src/utils/useful_functions';
import { getWarningLabel } from './NDAntennaPanel';
import { FormResetContext } from '../utils';

const getLabel = (label, cprops, level) => {
  // this should be the priority if label is mentioned
  // in field config it should have high priority
  // and then choice reponse level
  if (label != null) {
    return label;
  } else if (cprops && cprops['label'] != null) {
    return cprops['label'];
  } else if (level?.display_name) {
    return level.display_name;
  }
};

function NDSectorFormField(props) {
  const resetValue = useContext(FormResetContext);
  const { field, choices, ap, sm, refreshChoices, modified, setModified } =
    props;
  const { equipment } = choices;
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const { control, getValues, setValue } = useFormContext();
  const {
    attrName,
    getter,
    label,
    component = Form.Select,
    max,
    min,
    show,
    units,
    refreshesChoices,
    reCalculate = false,
    afterOnChange,
    componentProps,
    editable = true,
    warning,
    info,
    infoColor,
  } = field;
  const choiceObj = equipment[attrName];
  if (
    show != null &&
    !show({
      choices,
      formGetter: getValues,
      ap,
      sm,
      formSetter: setValue,
    })
  ) {
    return null;
  }
  const level = choiceObj;
  let editableField = editable;
  if (typeof editable === 'function') {
    editableField = editable({ choices, formGetter: getValues, ap });
  }

  let maxValue = max;
  let minValue = min;
  if (typeof max === 'function') {
    maxValue = max({ choices, formGetter: getValues, ap });
  }
  if (typeof min === 'function') {
    minValue = min({ choices, formGetter: getValues, ap });
  }
  const warningContent =
    warning && warning({ ap, sm, choices, formGetter: getValues });
  let infoLabel: any = null;
  if (info != null) {
    const text = info({ ap, sm, choices, formGetter: getValues });
    if (text != null) {
      infoLabel = (
        <Label
          color={infoColor ?? 'yellow'}
          pointing
          style={{ marginTop: 0, width: '100%' }}
        >
          {text}
        </Label>
      );
    }
  }

  let cprops: Record<string, any> = componentProps;
  if (typeof componentProps === 'function') {
    cprops = componentProps({
      ap,
      sm,
      choices,
      formGetter: getValues,
      refreshChoices: refreshChoices,
      formSetter: setValue,
    });
  }
  let ctrl: any = null;
  let finalLabel = '';
  if (typeof label === 'function') {
    finalLabel = label({ ap, choices, formGetter: getValues });
  } else {
    finalLabel = getLabel(label, cprops, level);
  }

  if (component === GenericScaledField) {
    ctrl = (
      <GenericScaledField
        key={getter}
        {...field}
        label={finalLabel}
        disabled={!editableField || !permissionWrite}
        {...cprops}
        units={units}
        recalc={reCalculate && modified}
        onAcceptChange={(e) => {
          if (afterOnChange) {
            afterOnChange(getValues(getter), { getValues, setValue }, choices);
          }
        }}
        defaultValue={get(ap, getter)}
        min={minValue}
        max={maxValue}
        setModified={setModified}
        watch={resetValue}
      ></GenericScaledField>
    );
  } else if (component === LimitControl) {
    ctrl = (
      <LimitControl
        key={getter}
        choices={choices}
        type="PMP"
        disabled={!editableField || !permissionWrite}
        modified={modified}
        componentProps={{ watch: resetValue }}
        min={minValue}
        max={maxValue}
        path={sm != null ? sm : ap}
        {...field}
        setModified={setModified}
        refreshChoices={refreshChoices}
      ></LimitControl>
    );
  }

  if (get(cprops, 'options', []).length === 1) {
    return (
      <Form.Field key={field.getter}>
        <label>{finalLabel}</label>
        <p>{get(cprops, 'options.0.text')}</p>
      </Form.Field>
    );
  }
  if (
    (level != null && level.choices != null && level.choices.length > 1) ||
    get(cprops, 'options', []).length > 1
  ) {
    ctrl = (
      <Controller
        control={control}
        name={getter}
        render={({ field: { ref, onChange, ...rest } }) => (
          <>
            <Form.Select
              options={level != null ? level.choices : cprops['options']}
              label={finalLabel}
              {...rest}
              disabled={!permissionWrite}
              onChange={(e, data) => {
                onChange(data.value);
                setModified(true);
                if (refreshesChoices) {
                  refreshChoices({ field });
                }
                if (afterOnChange) {
                  afterOnChange(data.value, { getValues, setValue }, choices);
                }
              }}
            />
            {warningContent && getWarningLabel(warningContent, modified)}
          </>
        )}
      />
    );
  } else if (
    level != null &&
    level.choices != null &&
    level.choices.length == 1
  ) {
    ctrl = (
      <>
        <label>{finalLabel}</label>
        <p>{getDisplayValue(field, level)}</p>
      </>
    );
  }
  return (
    <Form.Field key={field.getter}>
      {ctrl}
      {infoLabel}
      {/* {message} */}
    </Form.Field>
  );
}

export default NDSectorFormField;
